/**
 * Email Queue row + status badge helpers.
 * Extracted from EmailQueueDashboard to keep that file under the line limit.
 */
import { Button } from '../../components/ui/button';
import { TableCell, TableRow } from '../../components/ui/table';
import {
  Clock, CheckCircle, XCircle, RotateCw, Loader2, Ban, Send,
  SkipForward, ShieldAlert, MailCheck, MailX, Eye,
} from 'lucide-react';
import type { EmailQueueEntry } from '../../lib/api/emailQueue';

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: Clock },
  sending: { color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: Send },
  sent: { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle },
  delivered: { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: MailCheck },
  bounced: { color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: MailX },
  failed: { color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: XCircle },
  cancelled: { color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200', icon: Ban },
  skipped: { color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: SkipForward },
  rate_limited: { color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', icon: ShieldAlert },
};

// 'rate_limited' -> 'Rate Limited' (the default title-case would drop the space)
const STATUS_LABEL: Record<string, string> = { rate_limited: 'Rate Limited' };

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] || status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * The single status to display: a provider delivery outcome (bounced /
 * delivered) supersedes the queue's 'sent', so a row only ever shows one badge.
 */
export function effectiveStatus(entry: EmailQueueEntry): string {
  if (entry.bounced_at) return 'bounced';
  if (entry.delivered_at) return 'delivered';
  return entry.status;
}

export function StatusBadge({ status, opened = false, title }: { status: string; opened?: boolean; title?: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
  const Icon = cfg.icon;
  return (
    <span title={title} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {statusLabel(status)}
      {opened && <Eye className="h-3 w-3" />}
    </span>
  );
}

export function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface QueueRowProps {
  entry: EmailQueueEntry;
  cancellingId: number | null;
  retryingId: number | null;
  onCancel: (id: number) => void;
  onRetry: (id: number) => void;
  onView: (id: number) => void;
}

export function QueueRow({ entry, cancellingId, retryingId, onCancel, onRetry, onView }: QueueRowProps) {
  const sentOrFailedTime = entry.sent_at
    ? timeAgo(entry.sent_at)
    : entry.cancelled_at
      ? timeAgo(entry.cancelled_at)
      : entry.error_message
        ? <span className="text-xs text-red-600 break-words whitespace-normal">{entry.error_message}</span>
        : '-';

  return (
    <TableRow
      className="align-top cursor-pointer hover:bg-muted/50"
      onClick={() => onView(entry.id)}
      title="Click to view the email body">
      <TableCell className="font-medium text-sm max-w-[200px] break-all whitespace-normal">
        {entry.recipient_email}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground max-w-[180px] break-words whitespace-normal">
        {entry.recipient_company || '—'}
      </TableCell>
      <TableCell className="text-sm max-w-[280px] break-words whitespace-normal">
        {entry.subject}
      </TableCell>
      <TableCell>
        <StatusBadge
          status={effectiveStatus(entry)}
          opened={Boolean(entry.delivered_at && entry.opened_at)}
          title={entry.bounced_at ? (entry.bounce_reason || 'Bounced') : undefined}
        />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground break-words whitespace-normal">
        {entry.notification_type.replace(/_/g, ' ')}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
        {timeAgo(entry.created_at)}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground max-w-[200px] break-words whitespace-normal">
        {sentOrFailedTime}
      </TableCell>
      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          {entry.status === 'pending' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              disabled={cancellingId === entry.id}
              onClick={() => onCancel(entry.id)}
            >
              {cancellingId === entry.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
            </Button>
          )}
          {entry.status === 'failed' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              disabled={retryingId === entry.id}
              onClick={() => onRetry(entry.id)}
            >
              {retryingId === entry.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
